import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Close as CloseIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { getSeriesValue, normalizeSeriesItem } from '../utils/projectCompleteDetail';

const getComparableSeriesValue = (serie) => `${serie ?? ''}`.trim().toLowerCase();

const getExistingSeriesFromValidationResult = (validationResult, idArticulo) => {
    const validationSeries = Array.isArray(validationResult?.series) ? validationResult.series : [];

    return new Set(
        validationSeries.flatMap((item) => {
            const responseIdArticulo = item?.idArticulo ?? item?.IdArticulo;
            const hasDifferentArticle = responseIdArticulo
                && idArticulo
                && `${responseIdArticulo}` !== `${idArticulo}`;

            if (hasDifferentArticle) {
                return [];
            }

            if (Array.isArray(item?.serie)) {
                return item.serie;
            }

            if (Array.isArray(item?.Serie)) {
                return item.Serie;
            }

            return [item?.serie ?? item?.Serie];
        })
            .map((serie) => getComparableSeriesValue(serie))
            .filter(Boolean)
    );
};

const ProductSeriesDialog = ({ open, onClose, onValidate, onSave, productData, existingSeries, readOnly = false }) => {
    const [seriesText, setSeriesText] = useState('');
    const [seriesArray, setSeriesArray] = useState([]);
    const [hasValidated, setHasValidated] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);
    // readOnly comes true if idEntradaCompra !== null, meaning the product is already saved in the database and we are just viewing it.
    readOnly = readOnly || (productData && productData.idEntradaCompra !== null);

    React.useEffect(() => {
        if (open && existingSeries && existingSeries.length > 0) {
            const formattedSeries = existingSeries.map((serie) => ({
                ...normalizeSeriesItem(serie, productData?.idArticulo),
                error: false
            }));
            setSeriesArray(formattedSeries);
            setHasValidated(true);
        } else if (open) {
            setSeriesArray([]);
            setSeriesText('');
            setHasValidated(false);
        }
        setIsValidating(false);
        setIsSaving(false);
        setFeedback(null);
    }, [open, existingSeries, productData?.idArticulo]);

    const isSeriesValid = (serie, allSeries) => {
        if (!serie || serie.trim() === '') {
            return false;
        }

        const trimmedSerie = serie.trim();
        if (trimmedSerie.length < 3 || trimmedSerie.length > 50) {
            return false;
        }

        const duplicateCount = allSeries.filter((s) => getComparableSeriesValue(s) === getComparableSeriesValue(trimmedSerie)).length;
        if (duplicateCount > 1) {
            return false;
        }

        return /^[a-zA-Z0-9\-_.]+$/.test(trimmedSerie);
    };

    const validateSeries = async () => {
        if (readOnly) {
            return;
        }

        if (!seriesText.trim()) {
            setFeedback({
                severity: 'warning',
                message: 'Por favor ingrese al menos una serie.'
            });
            return;
        }

        const newSeriesInput = seriesText.split(/\r?\n/).filter((serie) => serie.trim() !== '');
        const allCurrentSeries = [...seriesArray.map((serie) => getSeriesValue(serie)), ...newSeriesInput.map((serie) => serie.trim())];

        let nextFeedback = null;
        let newSeriesArray = allCurrentSeries.map((serie) => {
            const trimmedSerie = serie.trim();
            const isValid = isSeriesValid(trimmedSerie, allCurrentSeries);
            return {
                ...normalizeSeriesItem(trimmedSerie, productData?.idArticulo),
                error: !isValid,
                errorMessage: isValid ? null : 'Formato invalido o duplicado'
            };
        });

        const locallyValidSeries = allCurrentSeries
            .map((serie) => serie.trim())
            .filter((serie) => isSeriesValid(serie, allCurrentSeries));

        if (onValidate && locallyValidSeries.length > 0) {
            try {
                setIsValidating(true);
                const validationResult = await onValidate({
                    productData,
                    series: locallyValidSeries.map((serie) => normalizeSeriesItem(serie, productData?.idArticulo))
                });
                const existingSeriesSet = getExistingSeriesFromValidationResult(validationResult, productData?.idArticulo);

                if (existingSeriesSet.size > 0) {
                    newSeriesArray = newSeriesArray.map((serie) => {
                        const seriesValue = getSeriesValue(serie);

                        if (existingSeriesSet.has(getComparableSeriesValue(seriesValue))) {
                            return {
                                ...serie,
                                error: true,
                                errorMessage: 'Serie ya registrada'
                            };
                        }

                        return serie;
                    });
                    nextFeedback = {
                        severity: 'warning',
                        message: validationResult?.message || 'Las siguientes series ya existen.'
                    };
                } else if (Number(validationResult?.success) === 0) {
                    nextFeedback = {
                        severity: 'warning',
                        message: validationResult?.message || 'No se pudieron validar todas las series.'
                    };
                }
            } catch (error) {
                setSeriesArray(newSeriesArray);
                setHasValidated(true);
                setFeedback({
                    severity: 'error',
                    message: 'No se pudieron validar las series existentes. Intenta de nuevo.'
                });
                return;
            } finally {
                setIsValidating(false);
            }
        }

        setSeriesArray(newSeriesArray);
        setHasValidated(true);
        setSeriesText('');
        setFeedback(nextFeedback);
    };

    const markExistingSeriesErrors = (validationResult) => {
        const existingSeriesSet = getExistingSeriesFromValidationResult(validationResult, productData?.idArticulo);

        if (existingSeriesSet.size === 0) {
            return false;
        }

        setSeriesArray((currentSeries) => currentSeries.map((serie) => {
            const seriesValue = getSeriesValue(serie);

            if (existingSeriesSet.has(getComparableSeriesValue(seriesValue))) {
                return {
                    ...serie,
                    error: true,
                    errorMessage: 'Serie ya registrada'
                };
            }

            return serie;
        }));

        setFeedback({
            severity: 'warning',
            message: validationResult?.message || 'Las siguientes series ya existen.'
        });

        return true;
    };

    const deleteSeries = (index) => {
        if (readOnly) {
            return;
        }

        const newArray = [...seriesArray];
        newArray.splice(index, 1);
        setSeriesArray(newArray);
    };

    const clearAll = () => {
        if (readOnly) {
            return;
        }

        setSeriesText('');
        setSeriesArray([]);
        setHasValidated(false);
        setIsValidating(false);
        setFeedback(null);
    };

    const handleSave = async () => {
        if (readOnly) {
            onClose();
            return;
        }

        const validSeries = seriesArray.filter((serie) => !serie.error);
        const requiredQuantity = parseInt(productData?.cantidad) || 0;

        if (validSeries.length === 0) {
            setFeedback({
                severity: 'warning',
                message: 'No hay series validas para guardar.'
            });
            return;
        }

        if (validSeries.length !== requiredQuantity) {
            setFeedback({
                severity: 'warning',
                message: `Debe tener exactamente ${requiredQuantity} series validas. Actualmente tiene ${validSeries.length} series validas.`
            });
            return;
        }

        try {
            setIsSaving(true);
            setFeedback(null);
            const result = await onSave({
                productData,
                series: validSeries.map(({ error, errorMessage, ...serie }) => serie)
            });

            if (result?.reason === 'series_existing') {
                const markedExistingSeries = markExistingSeriesErrors(result.validationResult);

                if (!markedExistingSeries) {
                    setFeedback({
                        severity: 'warning',
                        message: result.validationResult?.message || 'No se pudieron validar todas las series.'
                    });
                }

                return;
            }

            if (result === false || result?.success === false) {
                setFeedback({
                    severity: 'error',
                    message: 'No se pudieron guardar las series del articulo.'
                });
                return;
            }

            clearAll();
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!readOnly) {
            clearAll();
        }
        onClose();
    };

    const hasErrors = seriesArray.some((serie) => serie.error);
    const hasValidSeries = seriesArray.some((serie) => !serie.error);
    const validSeriesCount = seriesArray.filter((serie) => !serie.error).length;
    const requiredQuantity = parseInt(productData?.cantidad) || 0;
    const hasCorrectQuantity = validSeriesCount === requiredQuantity;
    const validateButtonLabel = isValidating
        ? 'Validando...'
        : (hasValidated ? 'Validar mas Series' : 'Validar Series');

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {readOnly ? 'Series del producto' : 'Configurar Series'} - {productData?.articulo || 'Producto'}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Producto:</strong> {productData?.articulo || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Descripcion:</strong> {productData?.descripcion || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Cantidad:</strong> {productData?.cantidad || 'N/A'}
                    </Typography>
                </Box>

                <Divider style={{ margin: '16px 0' }} />

                {feedback && (
                    <Box mb={2}>
                        <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                            {feedback.message}
                        </Alert>
                    </Box>
                )}

                {!readOnly && (
                    <>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Agregar Nuevas Series"
                                placeholder={'Ingrese las nuevas series, una por linea...\nEjemplo:\nSERIE001\nSERIE002\nSERIE003'}
                                value={seriesText}
                                onChange={(event) => setSeriesText(event.target.value)}
                                helperText={`Ingrese cada serie en una linea nueva. Formato: alfanumerico, guiones y puntos permitidos (3-50 caracteres). Total requeridas: ${requiredQuantity} series${hasValidated ? ` | Actuales: ${validSeriesCount}` : ''}`}
                            />
                        </Box>

                        <Box mb={2} display="flex" gap={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={validateSeries}
                                disabled={!seriesText.trim() || isValidating || isSaving}
                            >
                                {validateButtonLabel}
                            </Button>

                            {hasValidated && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={clearAll}
                                >
                                    Limpiar Todo
                                </Button>
                            )}
                        </Box>
                    </>
                )}

                {readOnly && seriesArray.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        Este producto no tiene series registradas.
                    </Typography>
                )}

                {hasValidated && seriesArray.length > 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {readOnly
                                ? `Series registradas (${validSeriesCount})`
                                : `Series Validadas (${validSeriesCount}/${requiredQuantity} requeridas)`}
                        </Typography>

                        {!readOnly && (
                            <Box mb={2} p={2} style={{
                                backgroundColor: hasCorrectQuantity ? '#d4edda' : (validSeriesCount > requiredQuantity ? '#fff3cd' : '#f8d7da'),
                                border: `1px solid ${hasCorrectQuantity ? '#28a745' : (validSeriesCount > requiredQuantity ? '#ffc107' : '#dc3545')}`,
                                borderRadius: '4px'
                            }}>
                                {hasCorrectQuantity ? (
                                    <Typography variant="body2" style={{ color: '#155724' }}>
                                        <strong>Cantidad correcta:</strong> Tiene exactamente {validSeriesCount} series validas (requeridas: {requiredQuantity})
                                    </Typography>
                                ) : validSeriesCount > requiredQuantity ? (
                                    <Typography variant="body2" style={{ color: '#856404' }}>
                                        <strong>Demasiadas series:</strong> Tiene {validSeriesCount} series validas, pero solo necesita {requiredQuantity}.
                                        Elimine {validSeriesCount - requiredQuantity} series para continuar.
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" style={{ color: '#721c24' }}>
                                        <strong>Faltan series:</strong> Tiene {validSeriesCount} series validas, necesita {requiredQuantity}.
                                        Agregue {requiredQuantity - validSeriesCount} series mas.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <List dense style={{ maxHeight: '300px', overflow: 'auto' }}>
                            {seriesArray.map((serie, index) => (
                                <ListItem
                                    key={`${getSeriesValue(serie)}-${index}`}
                                    style={{
                                        backgroundColor: serie.error ? '#ffeaa7' : '#d4edda',
                                        marginBottom: '4px',
                                        borderLeft: `4px solid ${serie.error ? '#e17055' : '#28a745'}`,
                                        borderRadius: '4px'
                                    }}
                                >
                                    <ListItemText
                                        primary={getSeriesValue(serie)}
                                        secondary={serie.error ? `Error: ${serie.errorMessage || 'Formato invalido o duplicado'}` : 'Valida'}
                                        style={{
                                            color: serie.error ? '#e17055' : '#28a745'
                                        }}
                                    />
                                    {!readOnly && (
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={() => deleteSeries(index)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    )}
                                </ListItem>
                            ))}
                        </List>

                        {!readOnly && hasErrors && (
                            <Typography variant="body2" color="error" style={{ marginTop: '8px' }}>
                                <strong>Hay series con errores:</strong> Eliminelas o corrijalas antes de guardar.
                            </Typography>
                        )}

                        {!readOnly && hasValidSeries && !hasErrors && (
                            <Typography
                                variant="body2"
                                style={{
                                    marginTop: '8px',
                                    color: hasCorrectQuantity ? '#28a745' : '#856404'
                                }}
                            >
                                {hasCorrectQuantity
                                    ? `${validSeriesCount} series validas listas para guardar.`
                                    : `${validSeriesCount} series validas, pero se requieren exactamente ${requiredQuantity}.`}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSaving}>
                    {readOnly ? 'Cerrar' : 'Cancelar'}
                </Button>
                {!readOnly && (
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={!hasValidated || !hasValidSeries || hasErrors || !hasCorrectQuantity || isValidating || isSaving}
                    >
                        {isSaving ? 'Guardando...' : `Guardar Series (${validSeriesCount}/${requiredQuantity})`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ProductSeriesDialog;
